import React, { useEffect, useState } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, CardActionArea,
  Chip, CircularProgress, Grid, Tooltip, IconButton,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { logError } from '../utils/errorLogger';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap');
`;

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m - 1]} ${+d}, ${y}`;
}

const SANCTIONING_LOGOS = {
  'AVP':   { bg: '#002B5C', color: '#FFD700', font: 'Arial Black, sans-serif', logo: '/avp.png' },
  'USAV':  { bg: '#002868', color: '#BF0A30', font: 'Arial Black, sans-serif', logo: '/usav.png' },
  'p1440': { bg: '#000000', color: '#C8A951', font: 'Georgia, serif',          logo: '/p1440.png' },
  'BVNE':  { bg: '#E8412A', color: 'white',   font: 'Arial Black, sans-serif', logo: '/bvne.png' },
};

export default function TournamentHome() {
  const [tournaments, setTournaments] = useState([]);
  const [pastTournaments, setPastTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [branding, setBranding] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadBranding() {
      try {
        const snap = await getDoc(doc(db, 'settings', 'branding'));
        setBranding(snap.exists() ? snap.data() : {});
      } catch {
        setBranding({});
      }
    }
    loadBranding();

    async function load() {
      try {
        const today = localToday();
        const q = query(collection(db, 'tournaments'), orderBy('date', 'asc'));
        const [snap, resultsSnap] = await Promise.all([
          getDocs(q),
          getDocs(collection(db, 'tournamentResults')),
        ]);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const resultIds = new Set(resultsSnap.docs.map(d => d.id));
        const now = new Date();
        // Upcoming
        setTournaments(list.filter(t => {
          if (!t.active) return false;
          if ((t.date || '') < today) return false;
          if (t.publish_date) {
            const publishDt = new Date(`${t.publish_date}T${t.publish_time || '00:00'}`);
            if (now < publishDt) return false;
          }
          return true;
        }));
        // Past with results — show only tournaments that ran AND have imported results AND are active
        const past = list
          .filter(t => t.active && (t.date || '') < today && resultIds.has(t.id))
          .sort((a, b) => (b.date || '').localeCompare(a.date || '')); // most recent first
        setPastTournaments(past);

        // Leagues sit on this page because it is where players already look for
        // "what can I sign up for". Same statuses the /leagues list shows, so
        // the two pages cannot disagree about what is on offer. Single orderBy,
        // no composite index.
        try {
          const lSnap = await getDocs(query(collection(db, 'leagues'), orderBy('startDate', 'asc')));
          setLeagues(lSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .filter(l => ['registration', 'active', 'playoffs'].includes(l.status)));
        } catch (e) {
          // A league read failing must not take the tournaments down with it.
          logError('TournamentHome', 'leagues: ' + (e.message || String(e)), e.stack || '');
        }
      } catch (err) {
        logError('TournamentHome', err.message || String(err), err.stack || '');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    async function checkAdmin() {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const roles = userDoc.data().roles || [];
          setIsAdmin(roles.includes('admin') || roles.includes('tournament_admin'));
        }
      } catch {}
    }

    load();
    checkAdmin();
  }, []);

  return (
    <>
      <style>{styles}</style>
      <Box sx={{ minHeight: '100vh', background: '#0D1E35' }}>

        {/* Hero */}
        <Box sx={{ px: 3, pt: 3 }}>
          <button
            onClick={() => navigate('/?dashboard=1')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(201,168,76,0.7)', fontFamily: '"Barlow", sans-serif',
              fontSize: '0.8rem', fontWeight: 600, letterSpacing: '1.5px',
              textTransform: 'uppercase', padding: 0,
            }}
            onMouseEnter={e => e.target.style.color = '#C9A84C'}
            onMouseLeave={e => e.target.style.color = 'rgba(201,168,76,0.7)'}
          >
            ← Back to Dashboard
          </button>
        </Box>

        <Box sx={{
          background: 'linear-gradient(180deg, #0a1828 0%, #0D1E35 100%)',
          borderBottom: '1px solid rgba(201,168,76,0.15)',
          py: 6, position: 'relative', textAlign: 'center',
        }}>
          <Container maxWidth="md">
            <Box sx={{ mb: 3 }}>
              <img src="/horzlogo.png" alt="Torch Beach Volleyball"
                   style={{ maxWidth: 300, width: '70vw', objectFit: 'contain' }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(2rem, 5vw, 3rem)',
              letterSpacing: 4, color: 'white', lineHeight: 1, mb: 1,
            }}>
              Upcoming Events
            </Typography>
            <Box sx={{ width: 60, height: 2, background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', mx: 'auto', my: 2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.9rem', letterSpacing: 0.5 }}>
              Select a tournament or league below to register and pay online.
            </Typography>
          </Container>
          {isAdmin && (
            <Tooltip title="Tournament Admin">
              <IconButton
                onClick={() => navigate('/tournament-admin')}
                sx={{
                  position: 'absolute', top: 16, right: 16,
                  color: 'rgba(201,168,76,0.5)',
                  '&:hover': { color: '#C9A84C', bgcolor: 'rgba(201,168,76,0.1)' },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Cards */}
        <Container maxWidth="lg" sx={{ py: 5 }}>
          {loading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#C9A84C' }} />
            </Box>
          )}

          {!loading && tournaments.length === 0 && pastTournaments.length === 0 && leagues.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <CalendarMonthIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
              <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.5rem', letterSpacing: 3, color: 'rgba(255,255,255,0.3)' }}>
                Nothing On Right Now
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontFamily: '"Barlow", sans-serif', fontWeight: 300, mt: 1 }}>
                Check back soon!
              </Typography>
            </Box>
          )}

          {!loading && tournaments.length === 0 && (pastTournaments.length > 0 || leagues.length > 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.95rem' }}>
                No tournaments scheduled right now.
              </Typography>
            </Box>
          )}

          {!loading && tournaments.length > 0 && branding && (
            <Grid container spacing={3}>
              {tournaments.map((t, i) => {
                const divisions = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
                return (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={t.id}>
                    <Card elevation={0} sx={{
                      height: '100%', borderRadius: 3,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(201,168,76,0.15)',
                      transition: 'transform .22s ease, border-color .22s ease, box-shadow .22s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: 'rgba(201,168,76,0.5)',
                        boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
                      },
                      animation: `fadeUp 0.55s ${0.08 + i * 0.08}s ease both`,
                      '@keyframes fadeUp': {
                        from: { opacity: 0, transform: 'translateY(22px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}>
                      <CardActionArea
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                        onClick={() => navigate(`/tournament/${t.id}`)}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          {/* Icon + Name */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{
                              background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)',
                              borderRadius: 2, p: 1, mr: 2,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              minWidth: 44, minHeight: 44,
                            }}>
                              {branding.chip_logo && branding.tournament_logo_data
                                ? <img src={`data:${branding.tournament_logo_mime};base64,${branding.tournament_logo_data}`} alt="" style={{ height: 28, objectFit: 'contain' }} />
                                : <EmojiEventsIcon sx={{ color: '#C9A84C', fontSize: 22 }} />
                              }
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{
                                  fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem',
                                  letterSpacing: 2, color: 'white', lineHeight: 1.1,
                                }}>
                                  {t.name}
                                </Typography>
                                {t.sanctioning_body && t.sanctioning_body !== 'None' && (() => {
                                  const logo = SANCTIONING_LOGOS[t.sanctioning_body];
                                  return (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                      backgroundColor: logo?.bg || 'rgba(255,255,255,0.15)',
                                      borderRadius: 0.5, px: 0.75, py: 0.25 }}>
                                      {logo?.logo ? (
                                        <img src={logo.logo} alt={t.sanctioning_body}
                                          style={{ height: 14, objectFit: 'contain', display: 'block' }}
                                          onError={e => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <Typography sx={{
                                          color: logo?.color || 'white', fontSize: '0.6rem', fontWeight: 900,
                                          letterSpacing: 1, fontFamily: logo?.font || 'Arial Black, sans-serif',
                                        }}>
                                          {t.sanctioning_body}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                              {t.location && (
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontFamily: '"Barlow", sans-serif', fontWeight: 300, mt: 0.5 }}>
                                  <LocationOnIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {t.location}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Date */}
                          <Typography sx={{ color: 'rgba(201,168,76,0.7)', fontSize: '0.8rem', fontFamily: '"Barlow", sans-serif', fontWeight: 600, mb: 1.5, letterSpacing: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                            {fmtDate(t.date)}
                          </Typography>

                          {/* Divisions */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {divisions.map(div => (
                              <Chip key={div} label={div} size="small" sx={{
                                fontSize: '0.6rem', fontWeight: 600, letterSpacing: 1,
                                fontFamily: '"Barlow", sans-serif',
                                backgroundColor: 'rgba(201,168,76,0.1)',
                                border: '1px solid rgba(201,168,76,0.25)',
                                color: 'rgba(201,168,76,0.7)',
                              }} />
                            ))}
                          </Box>
                        </CardContent>

                        {/* Arrow */}
                        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            border: '1px solid rgba(201,168,76,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#C9A84C', fontSize: '0.9rem',
                          }}>→</Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Leagues open for sign-up, or already running */}
          {!loading && leagues.length > 0 && (
            <Box sx={{ mt: tournaments.length > 0 ? 8 : 0 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography sx={{
                  fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
                  letterSpacing: 4, color: 'white', lineHeight: 1, mb: 1,
                }}>
                  Leagues
                </Typography>
                <Box sx={{ width: 50, height: 2, background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', mx: 'auto', mt: 1, mb: 2 }} />
              </Box>
              <Grid container spacing={3}>
                {leagues.map((l, i) => {
                  const open = l.status === 'registration';
                  const price = Number(l.price) || 0;
                  const memberPrice = Number(l.memberPrice) || 0;
                  return (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={l.id}>
                      <Card elevation={0} sx={{
                        height: '100%', borderRadius: 3,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(201,168,76,0.15)',
                        transition: 'transform .22s ease, border-color .22s ease, box-shadow .22s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: 'rgba(201,168,76,0.5)',
                          boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
                        },
                        animation: `fadeUp 0.55s ${0.08 + i * 0.08}s ease both`,
                      }}>
                        <CardActionArea onClick={() => navigate(`/leagues/${l.id}`)}
                          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                          <CardContent sx={{ p: 3, flexGrow: 1 }}>
                            <Chip
                              size="small"
                              label={open ? 'Registration open' : (l.status === 'playoffs' ? 'Play-offs' : 'In season')}
                              sx={{
                                mb: 1.5, fontFamily: '"Barlow", sans-serif', fontWeight: 600,
                                fontSize: '0.7rem', letterSpacing: 0.5,
                                color: open ? '#0D1E35' : '#C9A84C',
                                backgroundColor: open ? '#C9A84C' : 'rgba(201,168,76,0.12)',
                                border: open ? 'none' : '1px solid rgba(201,168,76,0.3)',
                              }} />
                            <Typography sx={{
                              fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.5rem',
                              letterSpacing: 2, color: 'white', lineHeight: 1.1, mb: 1,
                            }}>
                              {l.name}
                            </Typography>
                            {l.dayTimeDisplay && (
                              <Typography sx={{
                                color: 'rgba(255,255,255,0.55)', fontFamily: '"Barlow", sans-serif',
                                fontWeight: 300, fontSize: '0.85rem', mb: 1,
                              }}>
                                {l.dayTimeDisplay}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 15, color: 'rgba(201,168,76,0.7)' }} />
                              <Typography sx={{
                                color: 'rgba(255,255,255,0.7)', fontFamily: '"Barlow", sans-serif',
                                fontWeight: 300, fontSize: '0.85rem',
                              }}>
                                {l.format}s · {l.numWeeks} week{Number(l.numWeeks) === 1 ? '' : 's'} from {fmtDate(l.startDate)}
                              </Typography>
                            </Box>
                            {price > 0 && (
                              <Typography sx={{
                                color: '#C9A84C', fontFamily: '"Barlow", sans-serif',
                                fontWeight: 600, fontSize: '1rem', mt: 1.5,
                              }}>
                                ${(price / 100).toFixed(2)}
                                <Typography component="span" sx={{
                                  color: 'rgba(255,255,255,0.4)', fontFamily: '"Barlow", sans-serif',
                                  fontWeight: 300, fontSize: '0.75rem',
                                }}> / player{memberPrice > 0 ? ` · members $${(memberPrice / 100).toFixed(2)}` : ''}</Typography>
                              </Typography>
                            )}
                          </CardContent>
                          <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: '50%',
                              border: '1px solid rgba(201,168,76,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#C9A84C', fontSize: '0.9rem',
                            }}>→</Box>
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Past Tournaments — only those with imported results */}
          {!loading && pastTournaments.length > 0 && branding && (
            <Box sx={{ mt: 8 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography sx={{
                  fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
                  letterSpacing: 4, color: 'white', lineHeight: 1, mb: 1,
                }}>
                  Past Results
                </Typography>
                <Box sx={{ width: 50, height: 2, background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', mx: 'auto', mt: 1, mb: 2 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.85rem' }}>
                  See standings and final placements for completed tournaments.
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {pastTournaments.map((t, i) => {
                  const divisions = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
                  return (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={t.id}>
                      <Card elevation={0} sx={{
                        height: '100%', borderRadius: 3,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(201,168,76,0.1)',
                        transition: 'transform .22s ease, border-color .22s ease, box-shadow .22s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: 'rgba(201,168,76,0.4)',
                          boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
                        },
                      }}>
                        <CardActionArea
                          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                          onClick={() => navigate(`/tournament/${t.id}/results`)}
                        >
                          <CardContent sx={{ flexGrow: 1, p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{
                                background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)',
                                borderRadius: 2, p: 1, mr: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 44, minHeight: 44,
                              }}>
                                <EmojiEventsIcon sx={{ color: 'rgba(201,168,76,0.6)', fontSize: 22 }} />
                              </Box>
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography sx={{
                                    fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.15rem',
                                    letterSpacing: 2, color: 'rgba(255,255,255,0.85)', lineHeight: 1.1,
                                  }}>
                                    {t.name}
                                  </Typography>
                                  <Chip label="FINAL" size="small" sx={{
                                    height: 18, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1,
                                    backgroundColor: 'rgba(201,168,76,0.15)',
                                    color: '#C9A84C',
                                    border: '1px solid rgba(201,168,76,0.3)',
                                  }} />
                                </Box>
                                {t.location && (
                                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', fontFamily: '"Barlow", sans-serif', fontWeight: 300, mt: 0.5 }}>
                                    <LocationOnIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                    {t.location}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            <Typography sx={{ color: 'rgba(201,168,76,0.5)', fontSize: '0.8rem', fontFamily: '"Barlow", sans-serif', fontWeight: 600, mb: 1.5, letterSpacing: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                              {fmtDate(t.date)}
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {divisions.map(div => (
                                <Chip key={div} label={div} size="small" sx={{
                                  fontSize: '0.6rem', fontWeight: 600, letterSpacing: 1,
                                  fontFamily: '"Barlow", sans-serif',
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                  border: '1px solid rgba(201,168,76,0.15)',
                                  color: 'rgba(201,168,76,0.5)',
                                }} />
                              ))}
                            </Box>
                          </CardContent>

                          <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(201,168,76,0.6)', fontFamily: '"Barlow", sans-serif', fontWeight: 600, letterSpacing: 1 }}>
                              VIEW RESULTS
                            </Typography>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: '50%',
                              border: '1px solid rgba(201,168,76,0.25)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'rgba(201,168,76,0.7)', fontSize: '0.9rem',
                            }}>→</Box>
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Container>

        {/* Footer */}
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.18)', letterSpacing: 2, textTransform: 'uppercase', fontFamily: '"Barlow", sans-serif' }}>
            © {new Date().getFullYear()} Torch Beach Volleyball · Geneva, FL
          </Typography>
        </Box>

      </Box>
    </>
  );
}
